<template>
    <v-card elevation="2" class="ma-1">
        <v-card-text>
            <v-row dense align="center">
                <v-col cols="12" sm="6" md="3">
                    <v-text-field
                        v-model="busca"
                        label="Loja, indicado ou ID"
                        prepend-inner-icon="mdi-magnify"
                        outlined
                        dense
                        hide-details
                        clearable
                    />
                </v-col>
                <v-col cols="6" md="2">
                    <v-select
                        v-model="filtros.etapa"
                        :items="opcoesEtapa"
                        label="Etapa"
                        outlined
                        dense
                        hide-details
                        clearable
                    />
                </v-col>
                <v-col cols="6" md="2">
                    <v-select
                        v-model="filtros.comissao_status"
                        :items="opcoesComissao"
                        label="Comissão"
                        outlined
                        dense
                        hide-details
                        clearable
                    />
                </v-col>
                <v-col cols="12" md="auto">
                    <PeriodoFilter
                        v-model="periodo"
                        defaultPeriodo="Máximo"
                        label="Indicado em"
                        @update="recarregar"
                    />
                </v-col>
                <v-spacer/>
                <v-col v-if="podeEditar" cols="12" md="auto" class="d-flex" style="gap: 8px;">
                    <v-btn small outlined :loading="sincronizando" @click="sincronizar">
                        <v-icon left small>mdi-sync</v-icon>
                        Sincronizar
                    </v-btn>
                    <v-btn small color="primary" depressed @click="editar(null)">
                        <v-icon left small>mdi-plus</v-icon>
                        Nova indicação
                    </v-btn>
                </v-col>
            </v-row>

            <Totalizadores :itens="cards" :loading="carregandoTotais" class="my-3"/>

            <v-data-table
                :headers="headers"
                :items="indicacoes"
                :loading="carregando"
                :server-items-length="total"
                :options.sync="options"
                :footer-props="{ 'items-per-page-options': [10, 25, 50, 100] }"
                :mobileBreakpoint="0"
                disable-sort
                dense
                no-data-text="Nenhuma indicação"
            >
                <template v-slot:item.indicado_em="{ item }">
                    <div style="white-space: nowrap;">{{ formatDate(item.indicado_em, 'DD/MM/YYYY') }}</div>
                    <div v-if="item.origem === 'manual'" class="caption grey--text">manual</div>
                </template>

                <template v-slot:item.indicador="{ item }">
                    <div style="min-width: 150px;">
                        <a @click="$router.push(`/financeiro/${item.empresa_id}`)">#{{ item.empresa_id }}</a>
                        {{ item.empresa ? item.empresa.fantasia : '' }}
                    </div>
                </template>

                <template v-slot:item.indicado="{ item }">
                    <div style="min-width: 170px;">
                        {{ item.nome_indicado }}
                        <div v-if="item.contato" class="caption grey--text">
                            lead #{{ item.contato.id }}
                            <a v-if="whatsappLink(item.contato.whatsapp)" :href="whatsappLink(item.contato.whatsapp)" target="_blank">
                                · {{ item.contato.whatsapp }}
                            </a>
                        </div>
                        <div v-if="item.empresa_indicada_id" class="caption">
                            <a @click="$router.push(`/financeiro/${item.empresa_indicada_id}`)">#{{ item.empresa_indicada_id }}</a>
                            {{ item.empresa_indicada ? item.empresa_indicada.fantasia : '' }}
                        </div>
                    </div>
                </template>

                <template v-slot:item.etapa="{ item }">
                    <v-chip x-small dark :color="etapas[item.etapa].cor">{{ etapas[item.etapa].texto }}</v-chip>
                    <div v-if="item.cliente_em" class="caption grey--text" style="white-space: nowrap;">
                        pagou {{ formatDate(item.cliente_em, 'DD/MM/YYYY') }}
                    </div>
                </template>

                <template v-slot:item.comissao_status="{ item }">
                    <div style="min-width: 130px;">
                        <v-chip
                            x-small
                            :dark="comissoes[item.comissao_status].dark"
                            :outlined="comissoes[item.comissao_status].outlined"
                            :color="comissoes[item.comissao_status].cor"
                        >
                            {{ comissoes[item.comissao_status].texto }}
                        </v-chip>
                        <div class="caption grey--text">{{ detalheComissao(item) }}</div>
                    </div>
                </template>

                <template v-slot:item.valor_comissao="{ item }">
                    <span v-if="item.valor_comissao !== null" style="white-space: nowrap;">R$ {{ formatPrice(item.valor_comissao) }}</span>
                    <span v-else class="grey--text">—</span>
                </template>

                <template v-slot:item.acoes="{ item }">
                    <v-menu v-if="podeEditar" left offset-y>
                        <template v-slot:activator="{ on, attrs }">
                            <v-btn icon small v-bind="attrs" v-on="on">
                                <v-icon small>mdi-dots-vertical</v-icon>
                            </v-btn>
                        </template>
                        <v-list dense>
                            <v-list-item @click="editar(item)">
                                <v-list-item-title>Editar vínculo</v-list-item-title>
                            </v-list-item>
                            <v-list-item
                                v-if="!['cancelada', 'solicitada', 'paga'].includes(item.comissao_status)"
                                @click="abrirCancelamento(item)"
                            >
                                <v-list-item-title class="red--text">Cancelar comissão</v-list-item-title>
                            </v-list-item>
                            <v-list-item v-if="item.comissao_status === 'cancelada'" @click="reativar(item)">
                                <v-list-item-title>Reativar</v-list-item-title>
                            </v-list-item>
                        </v-list>
                    </v-menu>
                </template>
            </v-data-table>
        </v-card-text>

        <IndicacaoDialog
            :visible="dialogEdicao"
            :indicacao="emEdicao"
            @close="dialogEdicao = false"
            @salvo="aposSalvar"
        />

        <MotivoDialog
            :visible="!!emCancelamento"
            :carregando="executando"
            texto="A comissão desta indicação deixa de contar. Dá para reativar depois."
            titulo="Cancelar comissão"
            botao="Cancelar comissão"
            cor="red darken-1"
            @close="emCancelamento = null"
            @confirmar="cancelar"
        />
    </v-card>
</template>

<script>
import PeriodoFilter from '@/components/PeriodoFilter.vue';
import Totalizadores from '@/components/Totalizadores.vue';
import IndicacaoDialog from './IndicacaoDialog.vue';
import MotivoDialog from './MotivoDialog.vue';
import { COMISSOES, ETAPAS, opcoes, whatsappLink } from './status';

export default {
    name: 'TabIndicacoes',

    components: {
        IndicacaoDialog,
        MotivoDialog,
        PeriodoFilter,
        Totalizadores,
    },

    props: {
        podeEditar: { type: Boolean, default: false },
    },

    data: () => ({
        busca: '',
        filtros: {
            etapa: null,
            comissao_status: null,
        },
        periodo: {},
        carregando: false,
        carregandoTotais: false,
        sincronizando: false,
        executando: false,
        indicacoes: [],
        total: 0,
        totais: null,
        options: { page: 1, itemsPerPage: 25 },
        timerBusca: null,
        dialogEdicao: false,
        emEdicao: null,
        emCancelamento: null,
        etapas: ETAPAS,
        comissoes: COMISSOES,
        opcoesEtapa: opcoes(ETAPAS),
        opcoesComissao: opcoes(COMISSOES),
        headers: [
            { text: 'ID', value: 'id', width: '60px' },
            { text: 'Indicado em', value: 'indicado_em' },
            { text: 'Quem indicou', value: 'indicador' },
            { text: 'Indicado', value: 'indicado' },
            { text: 'Etapa', value: 'etapa' },
            { text: 'Comissão', value: 'comissao_status' },
            { text: 'Valor', value: 'valor_comissao', align: 'end' },
            { text: '', value: 'acoes', width: '40px' },
        ],
    }),

    computed: {
        params() {
            const p = {};

            Object.keys(this.filtros).forEach(k => {
                if (this.filtros[k]) p[k] = this.filtros[k];
            });

            if (this.busca && this.busca.trim()) p.q = this.busca.trim();
            if (this.periodo.start) p.start = this.periodo.start;
            if (this.periodo.end) p.end = this.periodo.end;

            return p;
        },

        cards() {
            const t = this.totais || {};
            const moeda = v => `R$ ${this.formatPrice(v || 0)}`;

            return [
                { label: 'Indicados', valor: t.indicados || 0, detalhe: t.canceladas ? `${t.canceladas} cancelada(s)` : '' },
                { label: 'Cadastros', valor: t.cadastros || 0, cor: 'orange' },
                { label: 'Clientes', valor: t.clientes || 0, cor: 'green' },
                { label: 'Em carência', valor: moeda(t.carencia), cor: 'amber' },
                { label: 'Disponível', valor: moeda(t.disponivel), cor: 'green' },
                { label: 'Solicitado', valor: moeda(t.solicitada), cor: 'blue', destaque: !!t.solicitada },
                { label: 'Pago', valor: moeda(t.paga) },
            ];
        },
    },

    watch: {
        filtros: {
            handler() {
                this.recarregar();
            },
            deep: true,
        },

        busca() {
            clearTimeout(this.timerBusca);
            this.timerBusca = setTimeout(() => this.recarregar(), 400);
        },

        options: {
            handler() {
                this.consultar();
            },
            deep: true,
        },
    },

    mounted() {
        this.carregarTotais();
    },

    beforeDestroy() {
        clearTimeout(this.timerBusca);
    },

    methods: {
        whatsappLink,

        recarregar() {
            this.carregarTotais();

            if (this.options.page !== 1) {
                this.options = { ...this.options, page: 1 };
                return;
            }

            this.consultar();
        },

        async consultar() {
            this.carregando = true;

            try {
                const { data } = await this.$http.get('gestao/indicacoes', {
                    params: { ...this.params, page: this.options.page, rows: this.options.itemsPerPage },
                });

                this.indicacoes = data.data;
                this.total = data.total;
            } catch (e) {
                this.notify('Não foi possível carregar as indicações', 'warning');
            } finally {
                this.carregando = false;
            }
        },

        async carregarTotais() {
            this.carregandoTotais = true;

            try {
                const { data } = await this.$http.get('gestao/indicacoes/totais', { params: this.params });
                this.totais = data;
            } catch (e) {
                this.totais = null;
            } finally {
                this.carregandoTotais = false;
            }
        },

        detalheComissao(item) {
            switch (item.comissao_status) {
            case 'carencia':
                return `libera ${this.formatDate(item.liberacao_em, 'DD/MM/YYYY')}`;
            case 'solicitada':
                return `solicitação nº ${item.solicitacao_id}`;
            case 'paga':
                return `em ${this.formatDate(item.pago_em, 'DD/MM/YYYY')}`;
            case 'cancelada':
                return item.cancelamento_motivo || '';
            default:
                return item.revisada ? 'conferida' : '';
            }
        },

        editar(item) {
            this.emEdicao = item;
            this.dialogEdicao = true;
        },

        aposSalvar() {
            this.dialogEdicao = false;
            this.recarregar();
        },

        abrirCancelamento(item) {
            this.emCancelamento = item;
        },

        async cancelar(motivo) {
            await this.acao(`gestao/indicacoes/${this.emCancelamento.id}/cancelar`, { motivo }, 'Comissão cancelada');
            this.emCancelamento = null;
        },

        async reativar(item) {
            const ok = await this.$root.$confirm(
                'Reativar',
                `Reativar a indicação #${item.id} (${item.nome_indicado})? A rotina não cancela mais esta indicação sozinha.`,
            );

            if (ok) {
                await this.acao(`gestao/indicacoes/${item.id}/reativar`, {}, 'Indicação reativada');
            }
        },

        async sincronizar() {
            this.sincronizando = true;

            try {
                const { data } = await this.$http.post('gestao/indicacoes/sincronizar');

                if (data.type !== 'success') {
                    this.notify(data.msg, 'warning');
                    return;
                }

                const r = data.data;
                const mudou = r.recuperadas + r.cadastros + r.clientes + r.liberadas;

                this.notify(mudou ? `${mudou} indicação(ões) atualizada(s)` : 'Nada mudou desde a última sincronização');
                this.recarregar();
            } catch (e) {
                this.notify('Não foi possível sincronizar', 'warning');
            } finally {
                this.sincronizando = false;
            }
        },

        async acao(url, corpo, sucesso) {
            this.executando = true;

            try {
                const { data } = await this.$http.post(url, corpo);

                if (data.type !== 'success') {
                    this.notify(data.msg, 'warning');
                    return;
                }

                this.notify(sucesso);
                this.recarregar();
            } catch (e) {
                this.notify('Não foi possível concluir a ação', 'warning');
            } finally {
                this.executando = false;
            }
        },
    },
};
</script>
